import { useState, useEffect } from 'react';
import { Lock, ArrowRight } from 'lucide-react';

// Simple password check - in production, use environment variables or server-side auth
const VALID_PASSWORD_HASH = 'SRCDA26!';

interface PasswordGateProps {
  children: React.ReactNode;
}

export function PasswordGate({ children }: PasswordGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shake, setShake] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const auth = sessionStorage.getItem('da_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(false);

    // Simulate a brief delay for effect
    await new Promise(resolve => setTimeout(resolve, 600));

    if (password === VALID_PASSWORD_HASH) {
      sessionStorage.setItem('da_auth', 'true');
      setIsAuthenticated(true);
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
    setIsLoading(false);
  };

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
      {/* Subtle white glow in center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/[0.02] rounded-full blur-[100px]" />

      {/* Horizontal line accents */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-sm px-6">
        {/* Logo section with white glow */}
        <div className="text-center mb-16">
          <div className="relative inline-block">
            {/* White glow behind logo */}
            <div className="absolute inset-0 -m-6 bg-white/10 rounded-full blur-2xl animate-pulse-slow" />

            {/* Logo */}
            <div className="relative">
              <img
                src="/assets/buttonup-logo.png"
                alt="Button Up Media"
                className="h-14 w-auto mx-auto filter brightness-0 invert drop-shadow-[0_0_40px_rgba(255,255,255,0.3)]"
              />
            </div>
          </div>

          <div className="mt-10 space-y-1">
            <h1 className="text-2xl font-light tracking-[0.2em] text-white uppercase">
              Digital Audit
            </h1>
            <div className="w-12 h-px bg-white/30 mx-auto" />
          </div>
        </div>

        {/* Password form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className={`relative ${shake ? 'animate-shake' : ''}`}>
            {/* Input container */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className={`h-4 w-4 transition-colors duration-300 ${
                  error ? 'text-red-500' : 'text-white/40'
                }`} />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(false);
                }}
                placeholder="PASSWORD"
                className={`w-full pl-11 pr-4 py-4 bg-transparent border-b-2 text-white text-sm tracking-[0.15em] placeholder-white/30 focus:outline-none transition-all duration-300 ${
                  error
                    ? 'border-red-500/50'
                    : 'border-white/20 focus:border-white/60'
                }`}
                autoFocus
              />
              {/* Focus glow line */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-white/0 group-focus-within:bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)] opacity-0 focus-within:opacity-100 transition-opacity" />
            </div>
          </div>

          {error && (
            <p className="text-center text-xs tracking-wider text-red-400/80 uppercase animate-fadeIn">
              Access Denied
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading || !password}
            className="relative w-full group"
          >
            <div className={`flex items-center justify-center gap-3 px-6 py-4 border border-white/20 text-white text-sm tracking-[0.2em] uppercase transition-all duration-300 ${
              isLoading || !password
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:bg-white hover:text-black hover:border-white'
            }`}>
              {isLoading ? (
                <div className="flex items-center gap-3">
                  <div className="h-4 w-4 border border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Verifying</span>
                </div>
              ) : (
                <>
                  <span>Enter</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                </>
              )}
            </div>
          </button>
        </form>

        {/* Footer */}
        <div className="mt-16 text-center">
          <p className="text-[10px] tracking-[0.3em] text-white/20 uppercase">
            Secured Access
          </p>
        </div>
      </div>

      {/* Corner accents */}
      <div className="absolute top-8 left-8 w-8 h-8 border-l border-t border-white/10" />
      <div className="absolute top-8 right-8 w-8 h-8 border-r border-t border-white/10" />
      <div className="absolute bottom-8 left-8 w-8 h-8 border-l border-b border-white/10" />
      <div className="absolute bottom-8 right-8 w-8 h-8 border-r border-b border-white/10" />

      {/* Custom animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
